import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, FieldControl, FormControl, Loading } from "spark-core-dx/components";
import { useValidationErrors } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  DESTINATION_FORM_GROUP_ID,
  buildDestinationDraft,
  buildDestinationFormMetadata,
  buildDestinationStepValues,
  ensureDestinationDefaults,
  getDeliveryRouteOptions,
  getDestinationIdFromValues,
  getDestinationItemName,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationSchemaContext,
  getDestinationSetupDefaults,
  getDestinationStepDescription,
  hasLoadedManagerMetadata,
  isCoreProfileMetadataReady,
  loadExistingDestinationOptions,
  loadDestinationById,
  loadDestinationManagerMetadata,
  normalizeDestinationFormChange,
  resolveMetadataItem,
  resetDestinationDownstreamCollections,
  saveDestination,
  sameDestinationState,
  validateDestinationDraft
} from "./destinationSetupStepUtils";
import {
  buildWizardValidationError,
  getId,
  getMetadataFields,
  keepObjectStateWhenEqual,
  requireServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const DESTINATION_SELECTION_MODE_NEW = "New";
const DESTINATION_SELECTION_MODE_EXISTING = "Existing";

const fieldValue = (nextValue) => {
  if (nextValue && typeof nextValue === "object") {
    return nextValue.Value ?? nextValue.value ?? nextValue._id ?? nextValue.id ?? "";
  }
  return nextValue ?? "";
};

const DestinationConfiguratorStep = ({
  context,
  onChange,
  registerStepController
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const { hasAndShowValidationErrors } = useValidationErrors();
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults, values),
    [context.productResourceDefaults, values, wizard]
  );
  const deliveryRouteOptions = useMemo(
    () => getDeliveryRouteOptions(context.productResourceDefaults || productResourceDefaults),
    [context.productResourceDefaults, productResourceDefaults]
  );
  const selectedDeliveryProductResourceKey = values.DeliveryProductResourceKey
    || values.deliveryProductResourceKey
    || productResourceDefaults.DeliveryProductResourceKey
    || productResourceDefaults.deliveryProductResourceKey
    || productResourceDefaults.selectedDeliveryProductResourceKey
    || "";
  const requiresDeliveryRouteSelection = deliveryRouteOptions.length > 0 && !selectedDeliveryProductResourceKey;
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const destinationMgr = resourceDomainContext?.managers?.[managerKey];
  const destinationIdFromValues = getDestinationIdFromValues(wizard, values, productResourceDefaults);
  const destinationSelectionModeFromValues = values.DestinationSelectionMode
    || values.destinationSelectionMode
    || (destinationIdFromValues ? DESTINATION_SELECTION_MODE_EXISTING : DESTINATION_SELECTION_MODE_NEW);
  const [destinationSelectionMode, setDestinationSelectionMode] = useState(destinationSelectionModeFromValues);
  const [selectedExistingDestinationId, setSelectedExistingDestinationId] = useState(destinationIdFromValues);
  const destinationId = selectedExistingDestinationId || destinationIdFromValues;
  const destinationLabel = getDestinationLabel(productResourceDefaults);
  const destinationItemName = getDestinationItemName(productResourceDefaults);
  const destinationStepDescription = getDestinationStepDescription(productResourceDefaults);
  const destinationSchemaContext = useMemo(
    () => getDestinationSchemaContext(wizard, values),
    [values, wizard]
  );
  const profileMetadataReady = isCoreProfileMetadataReady(coreContext);
  const [destination, setDestination] = useState(() =>
    buildDestinationDraft({ wizard, values, defaults: productResourceDefaults, coreContext })
  );
  const [existingDestinationOptions, setExistingDestinationOptions] = useState([]);
  const [loadingExistingDestinations, setLoadingExistingDestinations] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localError, setLocalError] = useState("");
  const destinationMgrRef = useRef(destinationMgr);
  const coreStateRef = useRef(coreState);
  const coreContextRef = useRef(coreContext);
  const destinationRef = useRef(destination);
  const productResourceDefaultsRef = useRef(productResourceDefaults);
  const loadKeyRef = useRef("");
  const isBusyRef = useRef(false);

  destinationMgrRef.current = destinationMgr;
  coreStateRef.current = coreState;
  coreContextRef.current = coreContext;
  destinationRef.current = destination;
  productResourceDefaultsRef.current = productResourceDefaults;
  isBusyRef.current = loading;

  useEffect(() => {
    setDestinationSelectionMode(destinationSelectionModeFromValues);
    setSelectedExistingDestinationId(destinationIdFromValues);
  }, [destinationIdFromValues, destinationSelectionModeFromValues, selectedDeliveryProductResourceKey]);

  useEffect(() => {
    setDestination((previous) => {
      if (getId(previous)) return previous;
      const next = buildDestinationDraft({ wizard, values, defaults: productResourceDefaults, coreContext });
      return keepObjectStateWhenEqual(previous, { ...previous, ...next });
    });
  }, [coreContext, productResourceDefaults, values, wizard]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = destinationMgrRef.current;
    const loadKey = `${managerKey}:${destinationId || "new"}:${destinationSelectionMode}:${selectedDeliveryProductResourceKey || "route-pending"}:${JSON.stringify(productResourceDefaults || {})}`;
    if (requiresDeliveryRouteSelection) {
      setLoading(false);
      setLocalError("");
      setMetadata(null);
      return undefined;
    }
    if (!activeMgr) {
      setLoading(false);
      setLocalError(`${destinationLabel} service is not available.`);
      return undefined;
    }
    if (!hasLoadedManagerMetadata(activeMgr) && !isCoreProfileMetadataReady(coreContextRef.current)) {
      setLoading(true);
      setLocalError("");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        const metadataPromise = loadDestinationManagerMetadata({
          manager: activeMgr,
          coreContext: coreContextRef.current
        });
        const destinationPromise = destinationId
          ? loadDestinationById({
            manager: activeMgr,
            destinationId,
            coreState: coreStateRef.current,
            fallbackMessage: `${destinationLabel} could not be loaded.`
          })
          : Promise.resolve(null);
        const [metadataResult, loadedDestination] = await Promise.all([metadataPromise, destinationPromise]);
        if (!mounted) return;
        requireServiceResponse(metadataResult, `${destinationLabel} metadata could not be loaded.`);
        const resolvedMetadata = resolveMetadataItem(metadataResult, destinationItemName)
          || resolveMetadataItem(activeMgr.uiMetadata, destinationItemName);
        setMetadata(resolvedMetadata);
        setDestination((previous) => {
          const source = loadedDestination || previous;
          const next = ensureDestinationDefaults(source, {
            wizard,
            defaults: productResourceDefaultsRef.current,
            coreContext: coreContextRef.current
          });
          return sameDestinationState(previous, next);
        });
        if (destinationId && !loadedDestination) {
          setLocalError(`The linked ${destinationLabel} could not be loaded. Save this step to create a new setup destination.`);
        }
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || `${destinationLabel} could not be loaded.`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [destinationId, destinationItemName, destinationLabel, destinationSelectionMode, managerKey, productResourceDefaults, profileMetadataReady, requiresDeliveryRouteSelection, selectedDeliveryProductResourceKey, wizard]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = destinationMgrRef.current;
    if (requiresDeliveryRouteSelection || !activeMgr || (!hasLoadedManagerMetadata(activeMgr) && !isCoreProfileMetadataReady(coreContextRef.current))) {
      setExistingDestinationOptions([]);
      setLoadingExistingDestinations(false);
      return undefined;
    }

    setLoadingExistingDestinations(true);
    const load = async () => {
      try {
        const options = await loadExistingDestinationOptions({
          manager: activeMgr,
          defaults: productResourceDefaultsRef.current,
          schemaContext: destinationSchemaContext,
          coreState: coreStateRef.current,
          fallbackMessage: `Existing ${destinationLabel} records could not be loaded.`
        });
        if (mounted) setExistingDestinationOptions(options);
      } catch (ex) {
        if (mounted) {
          setExistingDestinationOptions([]);
          setLocalError(ex?.message || `Existing ${destinationLabel} records could not be loaded.`);
        }
      } finally {
        if (mounted) setLoadingExistingDestinations(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [destinationLabel, destinationSchemaContext, managerKey, productResourceDefaults, profileMetadataReady, requiresDeliveryRouteSelection, selectedDeliveryProductResourceKey]);

  const formMetadata = useMemo(
    () => buildDestinationFormMetadata(metadata, productResourceDefaults),
    [metadata, productResourceDefaults]
  );
  const formFields = useMemo(() => getMetadataFields(formMetadata), [formMetadata]);
  const shouldShowDestinationForm = destinationSelectionMode !== DESTINATION_SELECTION_MODE_EXISTING || Boolean(destinationId);
  const canSaveDestinationForm = shouldShowDestinationForm && Boolean(formMetadata);
  const canContinue = Boolean(
    !requiresDeliveryRouteSelection
    && !loading
    && canSaveDestinationForm
    && (destinationSelectionMode !== DESTINATION_SELECTION_MODE_EXISTING
      || (!loadingExistingDestinations && destinationId))
  );

  const saveCurrentDestination = useCallback(async (currentValues = values) => {
    if (deliveryRouteOptions.length > 0 && !selectedDeliveryProductResourceKey) {
      const message = "Select a Destination Type before configuring the file destination.";
      setLocalError(message);
      throw buildWizardValidationError(message, { suppressSummary: true });
    }
    if (destinationSelectionMode === DESTINATION_SELECTION_MODE_EXISTING && !destinationId) {
      const message = `Select an existing ${destinationLabel} before continuing.`;
      setLocalError(message);
      throw buildWizardValidationError(message, { suppressSummary: true });
    }
    const activeMgr = destinationMgrRef.current;
    const destinationToSave = resetDestinationDownstreamCollections(ensureDestinationDefaults({
      ...currentValues,
      ...destinationRef.current
    }, {
      wizard,
      defaults: productResourceDefaultsRef.current,
      coreContext: coreContextRef.current
    }), productResourceDefaultsRef.current);
    if (!formMetadata) {
      const message = `${destinationLabel} metadata is not available.`;
      setLocalError(message);
      throw new Error(message);
    }
    const validationMessage = validateDestinationDraft({
      destination: destinationToSave,
      fields: formFields,
      hasAndShowValidationErrors,
      groupId: DESTINATION_FORM_GROUP_ID
    });
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    setLocalError("");
    const savedDestination = await saveDestination({
      manager: activeMgr,
      destination: destinationToSave,
      coreState: coreStateRef.current,
      fallbackMessage: `${destinationLabel} could not be saved.`
    });
    setDestination(savedDestination);
    return savedDestination;
  }, [deliveryRouteOptions.length, destinationId, destinationLabel, destinationSelectionMode, formFields, formMetadata, hasAndShowValidationErrors, selectedDeliveryProductResourceKey, values, wizard]);

  const prepareValues = useCallback(async (currentValues = values) => {
    const savedDestination = await saveCurrentDestination(currentValues);
    return buildDestinationStepValues({
      currentValues,
      destination: savedDestination,
      defaults: productResourceDefaultsRef.current
    });
  }, [saveCurrentDestination, values]);

  useEffect(() => {
    registerStepController?.({
      continueLabel: "Save & Continue",
      canContinue: () => canContinue,
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: prepareValues,
      prepareContinue: prepareValues
    });
  }, [canContinue, prepareValues, registerStepController]);

  useEffect(() => () => registerStepController?.(null), [registerStepController]);

  const handleFormChange = (value, fieldName, updatedItem) => {
    setDestination((previous) =>
      normalizeDestinationFormChange(previous, updatedItem, fieldName, value, productResourceDefaultsRef.current));
    onChange?.(fieldName, value);
  };

  const handleDeliveryTypeChange = (nextValue) => {
    const nextKey = fieldValue(nextValue);
    onChange?.("DeliveryProductResourceKey", nextKey);
    onChange?.("DestinationSelectionMode", DESTINATION_SELECTION_MODE_NEW);
    onChange?.("DestinationId", "");
    setDestinationSelectionMode(DESTINATION_SELECTION_MODE_NEW);
    setSelectedExistingDestinationId("");
    setDestination({});
    setMetadata(null);
    setExistingDestinationOptions([]);
    setLocalError("");
  };

  const handleDestinationSelectionModeChange = (nextValue) => {
    const nextMode = fieldValue(nextValue) || DESTINATION_SELECTION_MODE_NEW;
    setDestinationSelectionMode(nextMode);
    onChange?.("DestinationSelectionMode", nextMode);
    onChange?.("DestinationId", "");
    setSelectedExistingDestinationId("");
    setLocalError("");

    if (nextMode === DESTINATION_SELECTION_MODE_NEW) {
      setDestination(buildDestinationDraft({
        wizard,
        values,
        defaults: productResourceDefaultsRef.current,
        coreContext: coreContextRef.current
      }));
    } else {
      setDestination({});
    }
  };

  const handleExistingDestinationChange = (nextValue) => {
    const nextId = fieldValue(nextValue);
    setSelectedExistingDestinationId(nextId);
    onChange?.("DestinationId", nextId);
    setLocalError("");
  };

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title="File Destination"
        description={destinationStepDescription}
        actions={!requiresDeliveryRouteSelection ? (
          <div className="resource-wizard__mode-control" role="group" aria-label={`${destinationLabel} setup`}>
            <ButtonControl
              type="standard-small"
              className={`resource-wizard__mode-button${destinationSelectionMode === DESTINATION_SELECTION_MODE_EXISTING ? " active" : ""}`}
              onClick={() => handleDestinationSelectionModeChange(DESTINATION_SELECTION_MODE_EXISTING)}
              disabled={readOnly || saving || loadingExistingDestinations || existingDestinationOptions.length === 0}
            >
              Existing
            </ButtonControl>
            <ButtonControl
              type="standard-small"
              className={`resource-wizard__mode-button${destinationSelectionMode === DESTINATION_SELECTION_MODE_NEW ? " active" : ""}`}
              onClick={() => handleDestinationSelectionModeChange(DESTINATION_SELECTION_MODE_NEW)}
              disabled={readOnly || saving}
            >
              New
            </ButtonControl>
          </div>
        ) : null}
      />
      {deliveryRouteOptions.length > 0 ? (
        <div className="resource-wizard__fields">
          <div className="resource-wizard__field">
            <FieldControl
              className="resource-wizard__field-control"
              fieldMetadata={{
                FieldName: "DeliveryProductResourceKey",
                DisplayName: "Destination Type",
                Description: "Choose the file delivery route type for this outbound EDI flow.",
                FieldType: SystemTypes.UIFieldType.SelectList,
                IsRequired: true,
                DataJson: JSON.stringify(deliveryRouteOptions)
              }}
              selectData={deliveryRouteOptions}
              value={selectedDeliveryProductResourceKey}
              disabled={readOnly || saving}
              groupId={DESTINATION_FORM_GROUP_ID}
              onChange={handleDeliveryTypeChange}
              disableError
            />
          </div>
        </div>
      ) : null}
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : requiresDeliveryRouteSelection ? (
        <div className="resource-wizard__empty">
          <strong>Select a Destination Type.</strong>
          <p>Choose how this outbound EDI file will be delivered, then configure the matching destination connection.</p>
        </div>
      ) : (
        <div className="resource-wizard__linked-step-form">
          <div className="resource-wizard__fields">
            {destinationSelectionMode === DESTINATION_SELECTION_MODE_EXISTING ? (
              <div className="resource-wizard__field">
                <FieldControl
                  className="resource-wizard__field-control"
                  fieldMetadata={{
                    FieldName: "DestinationId",
                    DisplayName: `Existing ${destinationLabel}`,
                    Description: "Only destinations for the selected Entity Schema and destination type are available.",
                    FieldType: SystemTypes.UIFieldType.SearchSelectList,
                    IsRequired: true,
                    DataJson: JSON.stringify(existingDestinationOptions)
                  }}
                  selectData={existingDestinationOptions}
                  value={destinationId}
                  disabled={readOnly || saving || loadingExistingDestinations}
                  groupId={DESTINATION_FORM_GROUP_ID}
                  onChange={handleExistingDestinationChange}
                  disableError
                />
              </div>
            ) : null}
          </div>
          {shouldShowDestinationForm && formMetadata ? (
            <FormControl
              className="resource-wizard__linked-step-form-control"
              metadata={formMetadata}
              formFields={formFields}
              itemData={ensureDestinationDefaults(destination, {
                wizard,
                defaults: productResourceDefaults,
                coreContext
              })}
              disableFooter={true}
              disableMinWidth={true}
              disabled={readOnly}
              groupId={DESTINATION_FORM_GROUP_ID}
              tabLayout="top"
              onChange={handleFormChange}
              onSave={() => {}}
            />
          ) : shouldShowDestinationForm ? (
            <div className="resource-wizard__state">{destinationLabel} metadata is not available.</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default DestinationConfiguratorStep;
